import { BaseElementWithoutShadowDOM } from '@theme/base-element'
import { LiveRegionUtility } from '@theme/mixin-live-region-utility'

export class PredictiveSearch extends BaseElementWithoutShadowDOM {
  $liveRegionUtility = new LiveRegionUtility(this)
  resultsMaxHeight
  searchTerm = ''
  input
  searchButton
  predictiveSearchResults
  popularProducts
  overlay
  cachedResults = {}
  abortController = new AbortController()

  allPredictiveSearchInstances = []

  constructor() {
    super()
    this.handleOutsideEvent = this.handleOutsideEvent.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.searchForm = this.querySelector('form')
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    )
    this.popularProducts = this.querySelector('[data-popular-products]')
    this.searchButton = document.querySelector('[data-search-button]')
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search')
    this.setupEventListeners()
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this))
    this.input.addEventListener('focus', this.onFocus.bind(this))
    this.input.addEventListener('input', this.onChange.bind(this))
    this.searchButton.addEventListener('click', this.onFocus.bind(this))
    this.addEventListener('keyup', this.onKeyup.bind(this))
    this.addEventListener('keydown', this.onKeydown.bind(this))
    this.addEventListener('click', this.onNostoResultClick.bind(this), true)
    this.closest('details').addEventListener(
      'toggle',
      this.handleToggleButton.bind(this)
    )

    document.addEventListener('mousedown', this.handleOutsideEvent.bind(this))
    document.addEventListener('touchstart', this.handleOutsideEvent.bind(this))
  }

  get query() {
    return this.input.value.trim()
  }

  handleToggleButton(event) {
    if (!event.target.open) {
      this.close()
    }
  }

  onChange() {
    const newSearchTerm = this.query

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm)

    this.searchTerm = newSearchTerm

    if (!this.searchTerm.length) {
      this.predictiveSearchResults.innerHTML = this.popularProducts.innerHTML
      return
    }

    if (enableNostoPredictiveSearch) {
      if (this.searchTerm.length > 0) {
        this.getNostoSearchResults(this.searchTerm)
      }
    } else {
      this.getSearchResults(this.searchTerm)
    }
  }

  onNostoResultClick(event) {
    // nosto queues the click with the search it came from and sends it on the next page load,
    // so it has to be recorded here, not on the product page
    const link = event.target.closest(
      '#predictive-search-results-products-list a[href*="/products/"]'
    )
    if (!link || !this.nostoHits || typeof nostojs == 'undefined') return

    const handleFromUrl = (url) =>
      (String(url).match(/\/products\/([^/?#]+)/) || [])[1]
    const handle = handleFromUrl(link.getAttribute('href'))
    const hit = this.nostoHits.find((h) => handleFromUrl(h.url) == handle)
    if (!hit) return

    nostojs((api) => {
      api.recordSearchClick('autocomplete', {
        productId: hit.productId,
        url: hit.url,
      })
    })
  }

  onFormSubmit(event) {
    if (!this.query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault()
      return
    }

    // flags the search as organic, nosto-collection-search.js calls recordSearchSubmit on the search page
    try {
      sessionStorage.setItem('nosto-search-submit', this.query)
    } catch (e) {}
  }

  onFocus() {
    const currentSearchTerm = this.query
    this.open()

    if (!currentSearchTerm) {
      this.predictiveSearchResults.innerHTML = this.popularProducts.innerHTML
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange()
    } else {
      if (enableNostoPredictiveSearch) {
        if (this.searchTerm.length > 0) {
          this.getNostoSearchResults(this.searchTerm)
        }
      } else {
        this.getSearchResults(this.searchTerm)
      }
    }
  }

  onKeyup(event) {
    event.preventDefault()

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break
      case 'ArrowDown':
        this.switchOption('down')
        break
      case 'Enter':
        this.selectOption()
        break
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault()
    }
    if (event.code === 'Escape') {
      this.close(true)
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const currentButtonText = this.searchForTextElement?.innerText
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm)
      this.searchForTextElement.innerText = newButtonText
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return

    const moveUp = direction === 'up'
    this.selectedElement = this.querySelector('[aria-selected="true"]')

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, [data-predictive-search-item]')
    ).filter((element) => element.offsetParent !== null)
    let activeElementIndex = 0

    if (moveUp && !this.selectedElement) return

    let selectedElementIndex = -1
    let i = 0

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === this.selectedElement) {
        selectedElementIndex = i
      }
      i++
    }

    if (!moveUp && this.selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1
    }

    if (activeElementIndex === selectedElementIndex) return

    const activeElement = allVisibleElements[activeElementIndex]

    activeElement.setAttribute('aria-selected', true)
    if (this.selectedElement)
      this.selectedElement.setAttribute('aria-selected', false)

    this.input.setAttribute('aria-activedescendant', activeElement.id)
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    )

    if (selectedOption) selectedOption.click()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(
      'mousedown',
      this.handleOutsideEvent.bind(this)
    )
    document.removeEventListener(
      'touchstart',
      this.handleOutsideEvent.bind(this)
    )
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    fetch(
      `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          throw error
        }

        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML
        // Save bandwidth keeping the cache in all instances synced
        for (const predictiveSearchInstance of this
          .allPredictiveSearchInstances) {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup
        }
        this.renderSearchResults(resultsMarkup)

        // Publishes a custom search input event to Shopify Analytics.
        // This allows tracking user search queries in Shopify's analytics system.
        // The event name is 'analytics:search_input', and it sends the current search term as data.
        if (
          window.Shopify &&
          window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function'
        ) {
          window.Shopify.analytics.publish('analytics:search_input', {
            search_term: searchTerm,
            timestamp: new Date().toISOString(),
          })
        } else {
          console.warn('Shopify analytics API is not available.')
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return
        }
        throw error
      })
  }

  getNostoSearchResults(searchTerm, sessionParams) {
    //const popularProductsHTML = this.popularProducts.getElementsByClassName('predictive-search-results-products-list-container')[0].innerHTML;
    //const popularProductsHTML = this.popularProducts.getElementsByClassName('grid')[0].innerHTML;
    //console.log('popularProductsHTML', popularProductsHTML);
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()

    this.setLiveRegionLoadingState()

    fetch('https://search.nosto.com/v1/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `query ($query: String, $sessionParams: InputSearchQuery) {
            search(
              accountId: "shopify-13380845"
              query: $query
              sessionParams: $sessionParams
              products: { size: 4, from: 0 }
              keywords: { size: 2 }
              popularSearches: { 
                size: 5,
                emptyQueryMatchesAll: true
              }
            ) {
              products {
                fuzzy
                hits {
                  productId
                  url
                }
                total
                size
                from
              }
              keywords {
                hits {
                  keyword
                  _redirect
                  _highlight {
                    keyword
                  }
                }
              }
              popularSearches {
                hits {
                  query
                  total
                }
                total
              }
              query
            }
          }`,
        variables: {
          query: searchTerm,
          sessionParams: sessionParams,
        },
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        // The shopper kept typing: a slower, older response mustn't replace newer results
        if (searchTerm !== this.searchTerm) return

        console.log('nosto result')
        console.log(result)

        this.nostoHits = result.data.search.products.hits

        document.getElementById('predictive-search-products').innerText =
          'products'
        document.getElementById('predictive-search-queries').innerText =
          'suggested'
        this.renderNostoSearchResults(
          result.data.search.products.hits,
          searchTerm,
          result.data.search.products.total,
          true,
          '#predictive-search-results-products-list'
        )
        if (result.data.search.popularSearches) {
          this.renderNostoPopularSearches(
            result.data.search.popularSearches.hits,
            '#predictive-search-popular-searches-list'
          )
        }

        /* Start Analytics ------------------------------------------------ */
        /*
        Nosto.migrateToShopifyMarket({
          productSectionElement: '.nosto-product-info',
          productTitleElement: '.product-card__title',
          productUrlElement: '.nosto-product-url',
          productHandleAttribute: 'x-nosto-handle',
          priceElement: '.price-item',
          listPriceElement: '.price-item',
        })
          */

        nostojs((api) => {
          api.recordSearch(
            'autocomplete',
            {
              query: searchTerm,
              products: { size: 4, from: 0 },
            },
            result.data.search,
            {
              isKeyword: false,
            }
          )
        })

      })
  }

  renderNostoSearchResults(hits, searchTerm, total, fuzzy, containerSelector) {
    //console.log('hits')
    //console.log(hits)
    let html = ``

    // render emptpy grid
    let skeletonCards = ``
    for (let i = 0; i < hits.length; i++) {
      skeletonCards += `<div class="grid-item max-lg:[&:nth-child(3)]:hidden max-lg:[&:nth-child(4)]:hidden max-lg:[&:nth-child(2)]:bg-t-foreground-secondary"></div>`
    }
    //document.getElementById('product-grid').innerHTML = skeletonCards;
    document.querySelector(containerSelector).innerHTML = skeletonCards

    // asynconously fetch product card html by handle and replace skeleton card inner html with product card html as it comes in
    hits.forEach((hit, i) => {
      this.getProductHTMLByHandle(hit.url.split('/products/')[1], i, searchTerm)
    })
    // setLiveRegionResults() needs the shopify results count element, which nosto results don't have.
    // calling it threw here and stopped the nosto analytics after this render from running
    this.removeAttribute('loading')
  }

  getProductHTMLByHandle(handle, position, searchTerm) {
    console.log('handle', handle)
    const url = `${window.Shopify.routes.root}products/${handle}?view=searchproductcard`
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          throw error
        }
        //console.log('response', response)
        return response.text()
      })
      .then((responseText) => {
        if (searchTerm !== this.searchTerm) return // card from an older search
        //console.log('responseText', responseText)
        document.getElementsByClassName('grid-item')[position].innerHTML =
          responseText.replaceAll(
            'products/' + handle,
            'products/' + handle + '?autocomplete=1'
          )
      })
      .catch((error) => {
        //console.error('Error fetching product:', error)
      })
  }

  renderNostoPopularSearches(hits, containerSelector) {
    if (hits.lenght > 0) {
      let html = ``
      hits.forEach((hit) => {
        html += `<a href="/search?q=${hit.query}&type=product" class="text-base font-primary block lowercase">${hit.query}</a>`
      })
      document.querySelector(containerSelector).innerHTML = html
    }
  }

  setLiveRegionLoadingState() {
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text')

    this.$liveRegionUtility.announce({
      message: this.loadingText,
    })
    this.setAttribute('loading', true)
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)

    this.setLiveRegionResults()
    this.open()
  }

  setLiveRegionResults() {
    this.removeAttribute('loading')
    this.$liveRegionUtility.announce({
      message: this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      ).textContent,
    })
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('#header-search').getBoundingClientRect().bottom
    return this.resultsMaxHeight
  }

  getResultsVisibleHeight() {
    const announcementBarEl = document.querySelector('.announcement-bar')
    const headerEl = document.querySelector('header')

    const { height: announcementBarHeight } =
      announcementBarEl?.getBoundingClientRect() || { height: 0 }
    const { height: headerHeight } = headerEl?.getBoundingClientRect() || {
      height: 0,
    }

    return window.innerHeight - (announcementBarHeight + headerHeight)
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${this.getResultsVisibleHeight()}px` || `${this.getResultsMaxHeight()}px`
    this.setAttribute('open', true)
    document.body.classList.add('overflow-hidden')
    this.input.setAttribute('aria-expanded', true)
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm)
    document.body.classList.remove('overflow-hidden')
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
    }
    const selected = this.querySelector('[aria-selected="true"]')

    if (selected) selected.setAttribute('aria-selected', false)

    this.input.setAttribute('aria-activedescendant', '')
    this.removeAttribute('loading')
    this.removeAttribute('open')
    this.closest('details').removeAttribute('open')
    this.input.setAttribute('aria-expanded', false)
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style')
  }

  handleOutsideEvent(event) {
    const isClickInside =
      this.contains(event.target) || this.searchButton.contains(event.target)
    if (!isClickInside) {
      this.close()
    }
  }
}

window.customElements.define('predictive-search', PredictiveSearch)
